import React, { useState, useEffect } from 'react';
import { VideoCameraIcon, UserGroupIcon, CalendarIcon, ShieldCheckIcon, ShareIcon, XMarkIcon, PlayIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Title, Text } from '@tremor/react';
import { getVideoConferences, joinVideoConference, updateVideoConference, getConferenceRecordings } from '../../Requests/VideoConferenceRequests';
import ConferenceCreationModal from './ConferenceCreationModal';
import ShareLinkModal from './ShareLinkModal';
import AdminJoinModal from './AdminJoinModal';
import ViewRecordingModal from './ViewRecordingModal';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

// Custom Tooltip Component
const Tooltip = ({ children, text, className = "" }) => {
    const [show, setShow] = useState(false);
    
    return (
        <div 
            className="relative inline-block"
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && (
                <div className={`absolute z-50 px-2 py-1 text-xs text-white bg-iavisus-100 rounded shadow-lg whitespace-nowrap -top-8 left-1/2 transform -translate-x-1/2 ${className}`}>
                    {text}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-iavisus-100"></div>
                </div>
            )}
        </div>
    );
};

const VideoConferencesList = ({ project }) => {
    const [conferences, setConferences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [showRecordingModal, setShowRecordingModal] = useState(false);
    const [selectedConference, setSelectedConference] = useState(null);
    const [conferenceRecordings, setConferenceRecordings] = useState({});
    const [generatingLinks, setGeneratingLinks] = useState({});
    const { t } = useTranslation();
    const cookies = new Cookies();

    useEffect(() => {
        loadConferences();
    }, []);

    useEffect(() => {
        // Load recordings for completed conferences periodically
        const interval = setInterval(async () => {
            // Don't poll recordings when modal is open to prevent interference
            if (!selectedConference) {
                await loadRecordingsForCompletedConferences();
            } else {
                // Skip recordings poll when modal is open to prevent interference
            }
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, [conferences, selectedConference]);

    // Load recordings when modal is opened
    useEffect(() => {
        if (selectedConference && showRecordingModal) {
            // Load fresh recordings for the selected conference
            const loadSingleConferenceRecordings = async () => {
                try {
                    const response = await getConferenceRecordings(selectedConference.id);
                    setConferenceRecordings(prev => ({
                        ...prev,
                        [selectedConference.id]: response.data.recordings || []
                    }));
                } catch (error) {
                    console.error('Error loading recordings for modal:', error);
                }
            };
            loadSingleConferenceRecordings();
        }
    }, [selectedConference, showRecordingModal]);

    const loadRecordingsForCompletedConferences = async () => {
        const completedConferences = conferences.filter(conf => 
            conf.status === 'completed'
        );

        // Load recordings for completed conferences, but skip those with no-recording status
        for (const conference of completedConferences) {
            // Check has_recordings flag - if false, immediately mark as no recordings
            if (conference.has_recordings === false) {
                setConferenceRecordings(prev => ({
                    ...prev,
                    [conference.id]: []
                }));
                continue;
            }
            
            // Always fetch for completed conferences that may have recordings.
            // (Conferences with has_recordings === false are already handled above.)
            // We intentionally do not skip based on elapsed time, because recordings
            // can be reconciled and completed long after the call ended.
            try {
                const response = await getConferenceRecordings(conference.id);
                setConferenceRecordings(prev => ({
                    ...prev,
                    [conference.id]: response.data.recordings || []
                }));
            } catch (error) {
                console.error('Error loading recordings for conference:', conference.id, error);
            }
        }
    };

    const getRecordingStatus = (conference) => {
        // Only completed conferences have a recording outcome to show.
        if (conference.status !== 'completed') {
            return null;
        }

        // The backend definitively flags conferences that produced no recording.
        if (conference.has_recordings === false) {
            return {
                status: 'no-recording',
                text: t('VideoConference.NoRecording'),
                icon: XMarkIcon
            };
        }

        // Prefer freshly-polled recordings, but fall back to the recordings
        // embedded in the conference list response so the badge is correct
        // immediately on load — no async-fetch flicker, no missing badge.
        const recordings = conferenceRecordings[conference.id] ?? conference.recordings ?? [];

        // "Ready" means there is a composite that has actually been uploaded to
        // storage (spaces_path) and is therefore viewable.
        const compositeRecording = recordings.find(
            r => r.type === 'composite' && r.status === 'completed' && r.spaces_path
        );
        if (compositeRecording) {
            return {
                status: 'ready',
                text: '',
                icon: PlayIcon,
                recording: compositeRecording
            };
        }

        // A recording was made but no viewable composite exists yet — still
        // processing (it will flip to "View Recording" once the composite lands).
        return { status: 'processing', text: t('VideoConference.ProcessingRecording'), icon: ClockIcon };
    };

    const loadConferences = async () => {
        try {
            setLoading(true);
            
            const response = await getVideoConferences();
            
            // Handle paginated response - the actual array is in response.data.data
            const conferencesArray = response.data.data || response.data || [];
            
            // Filter conferences for this project if project is provided
            const filteredConferences = project 
                ? conferencesArray.filter(conf => conf.project_id === project.id)
                : conferencesArray;
            
            setConferences(filteredConferences);
            
            // Load recordings for completed conferences after initial load using optimized function
            setTimeout(() => {
                loadRecordingsForCompletedConferences();
            }, 1000);
        } catch (error) {
            console.error('Error loading video conferences:', error);
            setConferences([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const handleCreateConference = async (newConference) => {
        try {
            // Update state immediately with new conference
            setConferences(prev => [...prev, newConference]);
            
            // Close modal
            setShowCreateModal(false);
            
            // Refresh from server after delay to ensure consistency
            setTimeout(async () => {
                await loadConferences();
            }, 1000);
            
        } catch (error) {
            console.error('Error in handleCreateConference:', error);
            // If there's an error, just refresh the list
            await loadConferences();
            setShowCreateModal(false);
        }
    };

    const generateRoomUrl = async (conference, role = 'participant') => {
        const linkKey = `${conference.id}_${role}`;
        
        try {
            setGeneratingLinks(prev => ({ ...prev, [linkKey]: true }));

            // Get access token from backend with role
            const response = await joinVideoConference(conference.id, { role: role });
            const { access_token, room_name, participant_identity } = response.data;

            // Generate custom hosted room URL 
            const baseUrl = window.location.origin;
            const laravelToken = cookies.get('token');
            const currentLang = localStorage.getItem('i18nextLng') || 'en';
            const hostedUrl = `${baseUrl}/video-room-enhanced.html?token=${encodeURIComponent(access_token)}&room=${encodeURIComponent(room_name)}&identity=${encodeURIComponent(participant_identity)}&role=${encodeURIComponent(role)}&conference_id=${conference.id}&api_token=${encodeURIComponent(laravelToken)}&lang=${currentLang}`;
            
            // Open in new tab
            window.open(hostedUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');

        } catch (error) {
            console.error('Error generating room URL:', error);
            
            // Handle specific error cases
            if (error.response?.data?.code === 'MODERATOR_REQUIRED') {
                alert(t('VideoConference.ErrorMessages.ModeratorRequired', { error: error.response.data.error }));
            } else if (error.response?.data?.code === 'ROOM_EXPIRED') {
                alert(t('VideoConference.ErrorMessages.RoomExpired', { 
                    error: error.response.data.error, 
                    details: error.response.data.details || '' 
                }));
                // Refresh the conference list to show updated status
                loadConferences();
            } else if (error.response?.data?.code === 'CONFERENCE_ENDED') {
                alert(t('VideoConference.ErrorMessages.ConferenceEnded', { error: error.response.data.error }));
                // Refresh the conference list to show updated status
                loadConferences();
            } else if (error.response?.status === 410) {
                // Generic 410 Gone - room no longer available
                alert(t('VideoConference.ErrorMessages.RoomUnavailable'));
                loadConferences();
            } else {
                const errorMsg = error.response?.data?.error || t('VideoConference.ErrorMessages.GenerateUrlFailed', { role });
                alert(errorMsg);
            }
        } finally {
            setGeneratingLinks(prev => ({ ...prev, [linkKey]: false }));
        }
    };

    const handleJoinAsAdmin = (conference) => {
        setSelectedConference(conference);
        setShowAdminModal(true);
    };

    const handleJoinAsUser = (conference) => {
        generateRoomUrl(conference, 'participant');
    };

    const handleShareWaitingRoom = (conference) => {
        setSelectedConference(conference);
        setShowShareModal(true);
    };

    const handleViewRecording = (conference, recording) => {
        setSelectedConference(conference);
        setShowRecordingModal(true);
    };

    const handleEndConference = async (conference) => {
        const confirmEnd = window.confirm(t('VideoConference.EndConferenceConfirm', { title: conference.title }));
        
        if (!confirmEnd) return;

        try {
            setGeneratingLinks(prev => ({ ...prev, [`${conference.id}_ending`]: true }));
            
            // Update conference status to 'completed'
            await updateVideoConference(conference.id, { 
                status: 'completed' 
            });
            
            // Update the local state
            setConferences(prev => 
                prev.map(conf => 
                    conf.id === conference.id 
                        ? { ...conf, status: 'completed' }
                        : conf
                )
            );
            
            alert(t('VideoConference.EndConferenceSuccess'));
            
        } catch (error) {
            console.error('Error ending conference:', error);
            const errorMsg = error.response?.data?.error || t('VideoConference.EndConferenceError');
            alert(errorMsg);
        } finally {
            setGeneratingLinks(prev => ({ ...prev, [`${conference.id}_ending`]: false }));
        }
    };

    const handleTestMultiSession = async (conference) => {
        try {
            setGeneratingLinks(prev => ({ ...prev, [`${conference.id}_testing`]: true }));
            
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/conferences/${conference.id}/testing-urls`, {
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${cookies.get('token')}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to generate testing URLs');
            }
            
            const data = await response.json();
            
            // Open both URLs in separate tabs for testing
            window.open(data.admin_url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
            setTimeout(() => {
                window.open(data.participant_url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
            }, 1000); // Slight delay to prevent popup blocker
            
        } catch (error) {
            console.error('Error generating testing URLs:', error);
            alert(t('VideoConference.TestingUrlsError'));
        } finally {
            setGeneratingLinks(prev => ({ ...prev, [`${conference.id}_testing`]: false }));
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            scheduled: {
                style: 'bg-gray-100 text-gray-700 border border-gray-200',
                text: t('VideoConference.StatusScheduled'),
                icon: '📅'
            },
            active: {
                style: 'bg-iavisus-100 text-white border border-iavisus-100',
                text: t('VideoConference.StatusActive'),
                icon: '●'
            },
            completed: {
                style: 'bg-gray-50 text-gray-600 border border-gray-200',
                text: t('VideoConference.StatusEnded'),
                icon: '○'
            },
            cancelled: {
                style: 'bg-gray-100 text-gray-700 border border-gray-300',
                text: t('VideoConference.StatusCancelled'),
                icon: '×'
            }
        };

        const config = statusConfig[status] || {
            style: 'bg-gray-100 text-gray-800',
            text: status ? status.charAt(0).toUpperCase() + status.slice(1) : t('VideoConference.StatusUnknown'),
            icon: '❓'
        };

        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.style}`}>
                <span>{config.icon}</span>
                <span>{config.text}</span>
            </span>
        );
    };

    // Helper function to determine if conference can be joined
    const canJoinConference = (conference) => {
        const validStatuses = ['scheduled', 'active'];
        return validStatuses.includes(conference.status);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return t('VideoConference.NotScheduled');
        return new Date(dateString).toLocaleString();
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-iavisus-100"></div>
            </div>
        );
    }

    return (
        <div data-testid="video-conferences-container" className="mt-4">
            {/* Header with icon, title and description */}
            <div className="flex items-center space-x-3 mb-4">
                <VideoCameraIcon className="h-6 w-6 text-iavisus-100" />
                <Title className="text-lg font-semibold">
                    {t('VideoConference.Title')}
                </Title>
            </div>
            
            <Text className="text-gray-600 mb-6">
                {project ? t('VideoConference.ProjectConferencesSubtitle', { projectName: project.name }) : t('VideoConference.AllConferencesSubtitle')}
            </Text>

            <div className="flex justify-end items-center mb-6">
                <div></div>
                <Tooltip text={t('VideoConference.CreateConferenceTooltip')}>
                    <button
                        data-testid="video-conferences-create-btn"
                        onClick={() => setShowCreateModal(true)}
                        className="bg-iavisus-100 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-iavisus-200 flex items-center gap-2"
                    >
                        <VideoCameraIcon className="h-5 w-5" />
                        {t('VideoConference.CreateConferenceButton')}
                    </button>
                </Tooltip>
            </div>

            {conferences.length === 0 ? (
                <div data-testid="video-conferences-empty" className="text-center py-12">
                    <VideoCameraIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-iavisus-100">{t('VideoConference.NoConferencesTitle')}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('VideoConference.NoConferencesSubtitle')}
                    </p>
                    <div className="mt-6">
                        <Tooltip text={t('VideoConference.CreateConferenceTooltip')}>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="bg-iavisus-100 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-iavisus-200"
                            >
                                {t('VideoConference.CreateConferenceButton')}
                            </button>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {conferences.map(conference => (
                        <div key={conference.id} data-testid={`video-conference-card-${conference.id}`} className="bg-white border border-gray-200 rounded-lg p-6 shadow-tremor-card">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h3 className="text-lg font-medium text-iavisus-100">{conference.title}</h3>
                                    {conference.description && (
                                        <p className="text-sm text-gray-600 mt-1">{conference.description}</p>
                                    )}
                                    <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <CalendarIcon className="h-4 w-4" />
                                            {formatDateTime(conference.scheduled_for)}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <UserGroupIcon className="h-4 w-4" />
                                            {t('VideoConference.ParticipantCount', { current: conference.participants_count || 0, max: conference.settings?.max_participants || 10 })}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    {getStatusBadge(conference.status)}
                                    {(() => {
                                        const recordingStatus = getRecordingStatus(conference);
                                        if (recordingStatus) {
                                            const IconComponent = recordingStatus.icon;
                                            return (
                                                <div className="flex flex-col gap-1">
                                                    {recordingStatus.text && (
                                                        <div className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            recordingStatus.status === 'ready' 
                                                                ? 'bg-green-100 text-green-700 border border-green-200' 
                                                                : recordingStatus.status === 'no-recording'
                                                                ? 'bg-red-100 text-red-700 border border-red-200'
                                                                : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                        }`}>
                                                            <IconComponent className="h-3 w-3" />
                                                            <span>{recordingStatus.text}</span>
                                                        </div>
                                                    )}
                                                    {recordingStatus.status === 'ready' && (
                                                        <Tooltip text={t('VideoConference.ViewRecordingTooltip')}>
                                                            <button
                                                                onClick={() => handleViewRecording(conference, recordingStatus.recording)}
                                                                className="bg-green-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-green-700 flex items-center gap-1"
                                                            >
                                                                <PlayIcon className="h-3 w-3" />
                                                                {t('VideoConference.ViewRecordingButton')}
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                    {canJoinConference(conference) && (
                                        <div className="flex flex-col gap-1">
                                            <Tooltip text={t('VideoConference.AdminUrlTooltip')}>
                                                <button
                                                    onClick={() => handleJoinAsAdmin(conference)}
                                                    disabled={generatingLinks[`${conference.id}_admin`]}
                                                    className="bg-iavisus-100 text-white px-3 py-1 rounded text-xs font-medium hover:bg-iavisus-200 flex items-center gap-1 disabled:opacity-50"
                                                >
                                                {generatingLinks[`${conference.id}_admin`] ? (
                                                    <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full"></div>
                                                ) : (
                                                    <ShieldCheckIcon className="h-3 w-3" />
                                                )}
                                                {t('VideoConference.AdminUrlButton')}
                                                </button>
                                            </Tooltip>
                                            <Tooltip text={t('VideoConference.ShareLinkTooltip')}>
                                                <button
                                                    onClick={() => handleShareWaitingRoom(conference)}
                                                    className="bg-gray-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-gray-700 flex items-center gap-1"
                                                >
                                                    <ShareIcon className="h-3 w-3" />
                                                    {t('VideoConference.ShareLinkButton')}
                                                </button>
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Always show bottom section if there are settings or if it's an active conference */}
                            {(conference.settings || canJoinConference(conference)) && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <div className="flex justify-between items-center">
                                        <div className="flex flex-wrap gap-2 text-xs">
                                            {conference.settings?.auto_record && (
                                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">{t('VideoConference.SettingRecording')}</span>
                                            )}
                                            {conference.settings?.enable_chat && (
                                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">{t('VideoConference.SettingChat')}</span>
                                            )}
                                            {conference.settings?.enable_screen_share && (
                                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">{t('VideoConference.SettingScreenShare')}</span>
                                            )}
                                        </div>
                                        {canJoinConference(conference) && (
                                            <div className="ml-4">
                                                <Tooltip text={t('VideoConference.EndConferenceTooltip')}>
                                                    <button
                                                        onClick={() => handleEndConference(conference)}
                                                        disabled={generatingLinks[`${conference.id}_ending`]}
                                                        className="bg-gray-500 text-white px-3 py-1 rounded text-xs font-medium hover:bg-gray-600 flex items-center gap-1 disabled:opacity-50"
                                                    >
                                                    {generatingLinks[`${conference.id}_ending`] ? (
                                                        <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full"></div>
                                                    ) : (
                                                        <XMarkIcon className="h-3 w-3" />
                                                    )}
                                                    {t('VideoConference.EndConferenceButton')}
                                                    </button>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showCreateModal && (
                <ConferenceCreationModal
                    handleClose={() => setShowCreateModal(false)}
                    project={project}
                    onSave={handleCreateConference}
                />
            )}

            {showShareModal && (
                <ShareLinkModal
                    conference={selectedConference}
                    handleClose={() => {
                        setShowShareModal(false);
                        setSelectedConference(null);
                    }}
                />
            )}

            {showAdminModal && (
                <AdminJoinModal
                    conference={selectedConference}
                    handleClose={() => {
                        setShowAdminModal(false);
                        setSelectedConference(null);
                    }}
                />
            )}

            {showRecordingModal && (
                <ViewRecordingModal
                    conference={selectedConference}
                    recordings={conferenceRecordings[selectedConference?.id] || []}
                    handleClose={() => {
                        setShowRecordingModal(false);
                        setSelectedConference(null);
                    }}
                />
            )}
        </div>
    );
};

export default VideoConferencesList;